<template>
    <div id="form-container" ref="formContainer">
        <b-container fluid>
            <h1>
                Correct Name, SSN, or DOB
            </h1>
            <b-form class="mt-3">
                <b-row>
                    <b-col>
                        <BaseSelector v-model="change.changeType"
                                      label="Type of Correction"
                                      :options="opts.changeTypeOptions"
                                      slug="changeType"
                                      :v="$v.change.changeType"
                                      @input="loadAttachmentsForType"
                        />
                    </b-col>
                </b-row>
                <div v-if="change.changeType != null">
                    <b-row>
                        <b-col cols="4">
                            <BaseTextInput v-model="change.firstName"
                                           :v="$v.change.firstName"
                                           slug="firstName"
                                           label="First Name"
                            />
                        </b-col>
                        <b-col cols="4">
                            <BaseTextInput v-model="change.middleName"
                                           :v="$v.change.middleName"
                                           slug="middleName"
                                           label="Middle Name"
                            />
                        </b-col>
                        <b-col cols="4">
                            <BaseTextInput v-model="change.lastName"
                                           :v="$v.change.lastName"
                                           slug="lastName"
                                           label="Last Name"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12" lg="4">
                            <BaseDateOfBirthInput v-model="change.dateOfBirth"
                                                  :v="$v.change.dateOfBirth"
                                                  label="Date of Birth"
                                                  :messages="dobMessages"
                                                  slug="dob"
                                                  setId="dob"
                                                  tip="Format: MM/DD/YYYY"
                            />
                        </b-col>
                        <b-col cols="12" lg="4">
                            <BaseMaskedTextInput v-model="change.ssn"
                                                 :v="$v.change.ssn"
                                                 slug="ssn"
                                                 label="Social Security Number"
                                                 setId="ssn"
                                                 mask="###-##-####"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6">
                            <BaseMaskedTextInput v-model="change.phone"
                                                 :v="$v.change.phone"
                                                 slug="phone"
                                                 label="Cell Phone Number"
                                                 setId="phone"
                                                 mask="(###)###-####"
                            />
                        </b-col>
                        <b-col cols="6">
                            <BaseTextInput v-model="change.email"
                                           :v="$v.change.email"
                                           slug="email"
                                           label="Email"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="4" md="4" id="numberDiv">
                            <BaseTextInput v-model="change.certificateNumber"
                                           :v="$v.change.certificateNumber"
                                           label="Certificate Number"
                                           slug="number"
                                           setId="number"/>
                        </b-col>
                    </b-row>
                    <b-row v-if="change.changeType.includes('S')" class="mt-2">
                        <b-col cols="2">
                            SSN verification document:
                        </b-col>
                        <b-col cols="2">
                            <FileAttachment v-on:attachment-added="addSsnAttachment"
                                            :attachment-type-options="opts.ssnAttachmentOptions"
                                            describedBy="ssnAttachment"
                                            id="ssnAttachment"
                            />
                        </b-col>
                        <b-col>
                            Current attached file: <span>{{!change.ssnAttachment ? 'No file attached' : change.ssnAttachment.filename}}</span>
                        </b-col>
                    </b-row>
                    <b-row v-if="change.changeType.includes('N')" class="mt-2">
                        <b-col cols="2">
                            Name verification document:
                        </b-col>
                        <b-col cols="2">
                            <FileAttachment v-on:attachment-added="addNameAttachment"
                                            :attachment-type-options="opts.nameAttachmentOptions"
                                            describedBy="nameAttachment"
                                            id="nameAttachment"
                            />
                        </b-col>
                        <b-col>
                            Current attached file: <span>{{!change.nameAttachment ? 'No file attached' : change.nameAttachment.filename}}</span>
                        </b-col>
                    </b-row>
                  <b-row v-if="change.changeType.includes('D')" class="mt-2">
                    <b-col cols="2">
                      Date of Birth verification document:
                    </b-col>
                    <b-col cols="2">
                      <FileAttachment v-on:attachment-added="addDobAttachment"
                                      :attachment-type-options="opts.dobAttachmentOptions"
                                      describedBy="dobAttachment"
                                      id="dobAttachment"/>
                    </b-col>
                    <b-col>
                      Current attached file: <span>{{!change.dobAttachment ? 'No file attached' : change.dobAttachment.filename}}</span>
                    </b-col>
                  </b-row>
                    <b-row>
                        <b-col>
                            MDH will review the information provided to process your requested correction.
                            Please review carefully before submitting.
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col cols="2">
                            <b-button type="button" variant="primary" @click="showConfirmModal">Update</b-button>
                        </b-col>
                    </b-row>
                </div>
            </b-form>
            <b-modal id="confirm-modal" hide-header hide-footer>
                <h2>Confirm Change</h2>
                <hr/>
                <form ref="form" @submit.prevent="update">
                    Please review and confirm your changes before submitting.
                    <p class="mt-2">Press Confirm to submit this change request or Cancel to return to the request view.</p>
                    <div>
                        <b-button @click="closeConfirmModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                        <b-button type="submit" variant="primary">Confirm</b-button>
                    </div>
                </form>
            </b-modal>
        </b-container>
    </div>
</template>

<script>
import axios from "axios"
import axiosRetry from 'axios-retry'
import { required, requiredIf } from 'vuelidate/lib/validators'
import {toastService} from "@/mixins/toastService"
import FileAttachment from "@/components/templates/input/FileAttachment"
import BaseSelector from "@/components/templates/input/BaseSelector"
import minDate from '@/validators/minDate'
import maxDate from '@/validators/maxDate'

axiosRetry(axios, { retries: 0,
    retryDelay: axiosRetry.exponentialDelay  })

export default {
    data: () => ({
        dobMessages: {
            maxDate: 'Date of Birth cannot be in the future.',
            minDate: 'Date of Birth must be after 01/01/1900.'
        },
        opts: {
            changeTypeOptions: [
                {value: 'N', text: "Name"},
                {value: 'S', text: "SSN"},
                {value: 'D', text: "DOB"},
                {value: 'NS', text: "Name and SSN"},
                {value: 'ND', text: "Name and DOB"},
                {value: 'SD', text: "SSN and DOB"},
                {value: 'NSD', text: "Name, SSN, and DOB"}
            ],
            nameAttachmentOptions: [],
            ssnAttachmentOptions: [],
            dobAttachmentOptions: []
        },
        change: {
            ssnAttachment: null,
            nameAttachment:null,
            dobAttachment: null,
            firstName: null,
            middleName: null,
            lastName: null,
            dateOfBirth: null,
            phone: null,
            email: null,
            certificateNumber: null,
            ssn: null,
            changeType: null,
        }
    }),
    components:{
        BaseSelector,
        FileAttachment
    },
    validations() {
        return {
            change: {
                changeType: { required },
                firstName: { required },
                lastName: { required },
                middleName: { required: false },
                nameAttachment: { required: requiredIf(function(){ return this.isNameRequired}) },
                ssnAttachment: { required: requiredIf(function(){ return this.isSsnRequired}) },
                dobAttachment: { required: requiredIf(function(){ return this.isDobRequired})},
                ssn: { required },
                dateOfBirth: { required: true,
                    maxDate: maxDate(new Date()),
                    minDate: minDate(new Date('1900-01-01'))},
                phone: { required: false },
                email: { required },
                certificateNumber: { required },
            }
        }
    },
    methods: {
        showConfirmModal(){
            this.$bvModal.show('confirm-modal');
        },
        closeConfirmModal(){
            this.$bvModal.hide('confirm-modal');
        },
        loadAttachmentsForType() {
            if (this.change.changeType.includes('N')) {
                this.change.nameAttachment = null
                this.loadNameOptions()
            }
            if (this.change.changeType.includes('S')) {
                this.change.ssnAttachment = null
                this.loadSsnOptions()
            }
            if (this.change.changeType.includes('D')){
              this.change.dobAttachment = null
              this.loadDobOptions()
            }
        },
        loadNameOptions(){
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/nameTypes`, {ignoreHeader: true})
                .then(response => {
                    this.opts.nameAttachmentOptions = []
                    for(let type of response.data){
                        this.opts.nameAttachmentOptions.push({
                            value: type.code,
                            text: type.description
                        })
                    }
                })
        },
        loadSsnOptions(){
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/ssnTypes`, {ignoreHeader: true})
                .then(response => {
                    this.opts.ssnAttachmentOptions = []
                    for(let type of response.data){
                        this.opts.ssnAttachmentOptions.push({
                            value: type.code,
                            text: type.description
                        })
                    }
                })
        },
      loadDobOptions(){
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/dobTypes`, {ignoreHeader: true})
            .then(response => {
              this.opts.dobAttachmentOptions = []
              for(let type of response.data){
                this.opts.dobAttachmentOptions.push({
                  value: type.code,
                  text: type.description
                })
              }
            })
      },
        addSsnAttachment(value){
            this.change.ssnAttachment = value
        },
        addNameAttachment(value){
            this.change.nameAttachment = value
        },
        addDobAttachment(value){
            this.change.dobAttachment = value
        },
        update() {
            this.$v.$touch()
            if (!this.$v.$invalid) {
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/change/unclaimed/`, this.change)
                    .then(response => {
                        toastService.showRootToast("Name, SSN, or DOB Correction", "Your request has been submitted.", "success", this.$root)
                        this.$router.push({path: "/"});
                    }) .catch(error => {
                        toastService.showRootToast("Error", "An error has occurred " , "danger", this.$root)
                    }).finally(()=>{
                        this.closeConfirmModal();
                })
            }
        },
    },
    created() {
        document.title = "Correct Name, SSN, or DOB - NAR"
    },
    computed: {
        isNameRequired() {
            return this.change.changeType.includes('N')
        },
        isSsnRequired() {
            return this.change.changeType.includes('S')
        },
        isDobRequired() {
            return this.change.changeType.includes('D')
        }
    },
    mixins: [
        toastService
    ],
}
</script>